{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://davisjam.github.io/model-based-agentic-software-engineering/workbench/mage-transaction.schema.json",
  "title": "MAGE model transaction",
  "description": "An all-or-nothing change to a model system. Application order is fixed: parse -> verify base -> apply to a temporary IR -> validate the ENTIRE resulting system -> commit. Any failure leaves the current system unchanged. Semantics in SEMANTICS.md section 9.",
  "type": "object",
  "required": ["transaction"],
  "additionalProperties": false,
  "properties": {
    "transaction": {
      "type": "object",
      "required": ["base", "operations"],
      "additionalProperties": false,
      "properties": {
        "base": {
          "description": "Hash of the CANONICAL IR the operations were computed against -- never of the file bytes. Comments and key order are preserved on write, so a file hash would be invalidated by cosmetic edits. A mismatch is a LOUD failure, never a best-effort merge: an agent whose premise has changed must recompute. The algorithm is NAMED in the value because this is a concurrency token, not a security boundary -- v0.1 emits fnv1a64 (see src/ir/hash.ts for why SubtleCrypto was rejected), and the prefix stops it being read as a cryptographic claim.",
          "type": "string",
          "pattern": "^(fnv1a64|sha256):[0-9a-f]{16,64}$"
        },
        "target": {
          "description": "Branch to apply to. 'main' is the authoritative system; any other name is a hypothesis branch for what-if analysis.",
          "type": "string",
          "default": "main"
        },
        "rationale": {
          "description": "Why this change, in the requester's terms. Shown in the proposal preview and retained in the audit trail.",
          "type": "string"
        },
        "semantics": {
          "type": "object",
          "additionalProperties": false,
          "properties": {
            "atomic": {
              "description": "Always true in v0.1. Present so a future non-atomic mode cannot be introduced silently.",
              "const": true
            }
          }
        },
        "operations": {
          "type": "array",
          "minItems": 1,
          "items": { "$ref": "#/$defs/operation" }
        }
      }
    }
  },

  "$defs": {
    "id": { "type": "string", "pattern": "^[A-Za-z_][A-Za-z0-9_.\\[\\]/-]*$", "maxLength": 160 },
    "scalar": { "oneOf": [{ "type": "string" }, { "type": "boolean" }, { "type": "integer" }] },

    "operation": {
      "description": "Ids are immutable (V2): there is no rename operation. set-label changes the human-readable label; changing identity means delete plus add, and the consequences are then visible in the diff.",
      "oneOf": [
        { "$ref": "#/$defs/opSetLabel" },
        { "$ref": "#/$defs/opSetProperty" },
        { "$ref": "#/$defs/opAddEntity" },
        { "$ref": "#/$defs/opDeleteEntity" },
        { "$ref": "#/$defs/opAddState" },
        { "$ref": "#/$defs/opDeleteState" },
        { "$ref": "#/$defs/opAddTransition" },
        { "$ref": "#/$defs/opDeleteTransition" },
        { "$ref": "#/$defs/opAddRelation" },
        { "$ref": "#/$defs/opDeleteRelation" },
        { "$ref": "#/$defs/opSetPurpose" },
        { "$ref": "#/$defs/opSetDescription" },
        { "$ref": "#/$defs/opAddModel" },
        { "$ref": "#/$defs/opAddModelEntity" },
        { "$ref": "#/$defs/opDeleteModel" },
        { "$ref": "#/$defs/opAddNote" },
        { "$ref": "#/$defs/opSetQuantityValue" },
        { "$ref": "#/$defs/opSetEntityType" },
        { "$ref": "#/$defs/opSaveQuery" },
        { "$ref": "#/$defs/opDeleteQuery" }
      ]
    },

    "opSetLabel": {
      "type": "object",
      "required": ["op", "id", "value"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-label" },
        "id": { "$ref": "#/$defs/id" },
        "value": { "type": "string" }
      }
    },
    "opSetQuantityValue": {
      "description": "Re-declare one quantity's point value. `value` is a unit-bearing literal in one of the quantity's declared dimension's units -- a bare number would force the op to choose a unit, and the only defensible choice is the dimension's base. Refused when the quantity declares a range or an expression: replacing either with a point discards a declaration the author made.",
      "type": "object",
      "required": ["op", "id", "value"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-quantity-value" },
        "id": { "$ref": "#/$defs/id" },
        "value": { "type": "string" }
      }
    },
    "opSetProperty": {
      "type": "object",
      "required": ["op", "id", "name"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-property" },
        "id": { "$ref": "#/$defs/id" },
        "name": { "type": "string" },
        "value": { "$ref": "#/$defs/scalar" },
        "domain": { "$ref": "#/$defs/id" },
        "unset": {
          "description": "Remove the property. Mutually exclusive with value.",
          "const": true
        }
      }
    },
    "opAddEntity": {
      "type": "object",
      "required": ["op", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-entity" },
        "id": { "$ref": "#/$defs/id" },
        "type": { "type": "string" },
        "label": { "type": "string" },
        "contains": { "type": "array", "items": { "$ref": "#/$defs/id" } }
      }
    },
    "opDeleteEntity": {
      "type": "object",
      "required": ["op", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-entity" },
        "id": { "$ref": "#/$defs/id" },
        "cascade": {
          "description": "false (default) means the transaction FAILS if anything still references this id -- dangling references are never created silently.",
          "type": "boolean",
          "default": false
        }
      }
    },
    "opAddState": {
      "type": "object",
      "required": ["op", "machine", "state"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-state" },
        "machine": { "$ref": "#/$defs/id" },
        "state": { "$ref": "#/$defs/id" },
        "label": { "type": "string" }
      }
    },
    "opDeleteState": {
      "type": "object",
      "required": ["op", "machine", "state"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-state" },
        "machine": { "$ref": "#/$defs/id" },
        "state": { "$ref": "#/$defs/id" },
        "cascade": { "type": "boolean", "default": false }
      }
    },
    "opAddTransition": {
      "type": "object",
      "required": ["op", "machine", "from", "to"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-transition" },
        "machine": { "$ref": "#/$defs/id" },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "label": { "type": "string" },
        "sync": {
          "description": "Synchronization only; must resolve to a declared event (V1). Named sync: because YAML 1.1 coerces a bare `on` key to boolean true.",
          "$ref": "#/$defs/id"
        },
        "requires": { "type": "object" },
        "effects": { "type": "object", "additionalProperties": { "type": "string" } }
      }
    },
    "opDeleteTransition": {
      "type": "object",
      "required": ["op", "machine"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-transition" },
        "machine": { "$ref": "#/$defs/id" },
        "index": {
          "description": "Position in the machine's transitions list. Use when from/to/on do not identify one uniquely -- nondeterminism makes duplicates legal.",
          "type": "integer",
          "minimum": 0
        },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "sync": { "$ref": "#/$defs/id" }
      }
    },
    "opAddRelation": {
      "type": "object",
      "required": ["op", "model", "from", "to", "type"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-relation" },
        "model": { "$ref": "#/$defs/id" },
        "id": { "$ref": "#/$defs/id" },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "type": { "$ref": "#/$defs/id" },
        "label": { "type": "string" }
      }
    },
    "opDeleteRelation": {
      "type": "object",
      "required": ["op", "model"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-relation" },
        "model": { "$ref": "#/$defs/id" },
        "id": { "$ref": "#/$defs/id" },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "type": { "$ref": "#/$defs/id" }
      }
    },
    "opSetPurpose": {
      "type": "object",
      "required": ["op", "scope", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-purpose" },
        "scope": { "enum": ["model", "machine"] },
        "id": { "$ref": "#/$defs/id" },
        "question": { "type": "string" },
        "represents": { "type": "array", "items": { "type": "string" } },
        "omits": {
          "description": "Checked against the model's actual vocabulary on validation, not merely asserted (V24). A transaction that declares an omission the model contradicts FAILS.",
          "type": "array",
          "items": { "type": "string" }
        }
      }
    },
    "opSetDescription": {
      "description": "Write or revise a model's 'Model in words' -- the stored, deliberately-authored natural-language description beside the formal representation. NOT purpose: question/represents/omits are semantic and hashed; the description is prose with the epistemic status of a code comment, excluded from the canonical hash, so writing it commits without advancing the semantic revision. The human surface renders it verbatim and never generates one: absent means absent. An empty value retracts the field.",
      "type": "object",
      "required": ["op", "scope", "id", "value"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-description" },
        "scope": { "enum": ["model", "machine"] },
        "id": { "$ref": "#/$defs/id" },
        "value": { "type": "string" }
      }
    },
    "opAddModel": {
      "description": "Add a purposeful model. It takes NO purpose block: set-purpose already owns question/represents/omits, and a second writer of those three fields would be a second source of truth. Send add-model and set-purpose in one transaction -- atomicity makes the pair indivisible, so a question-less model is never committed. Entity ids are checked by V3 at the validation stage, as they are for add-relation.",
      "type": "object",
      "required": ["op", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-model" },
        "id": { "$ref": "#/$defs/id" },
        "label": { "type": "string" },
        "entities": {
          "description": "Ids of system-level entities this model includes. Models reference; they never redeclare (V3).",
          "type": "array",
          "items": { "$ref": "#/$defs/id" }
        }
      }
    },
    "opAddModelEntity": {
      "description": "Include an already-declared entity in a graph model's membership -- the one operation that extends a model's `entities:` list. It creates no entity: models reference entities and never redeclare them (V3), so adding a component is two acts, add-entity then this one, for each model that should see it. That is not friction to be smoothed away -- a model is a purposeful reduction, and an op that put a new id into every model would be guessing which reductions the author meant it to join. Refused when the entity is already a member; an unknown id is checked by V3 at the validation stage, as it is for add-model and add-relation. Without this op, V40 makes a model's membership unextendable: add-relation to a non-member is a finding, and nothing could clear it.",
      "type": "object",
      "required": ["op", "model", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-model-entity" },
        "model": { "$ref": "#/$defs/id" },
        "id": { "$ref": "#/$defs/id" }
      }
    },
    "opDeleteModel": {
      "description": "Remove a purposeful model. There is deliberately no cascade: relations are flattened across models and carry their owning model, so a relation is not a pointer the model could tidy away -- it is a claim the model makes. Deleting a claim and refusing to delete it assert different things, so the transaction FAILS while the model still asserts any relation, naming each. Delete them explicitly, in the same transaction if you like, and the diff shows which claims went.",
      "type": "object",
      "required": ["op", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-model" },
        "id": { "$ref": "#/$defs/id" }
      }
    },
    "opAddNote": {
      "description": "Attach a note to an entity, a model or a relation -- the three objects that carry annotation. NON-SEMANTIC (A1): the canonical hash excludes annotation, so this commits WITHOUT advancing the semantic revision and does not invalidate a pending transaction. An assumption written as a note is context, not a constraint; to bind an analysis result it must be represented as a property, a variable or a guard.",
      "type": "object",
      "required": ["op", "scope", "note"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "add-note" },
        "scope": { "enum": ["entity", "model", "relation"] },
        "id": {
          "description": "The entity or model id. For scope 'relation' this is the relation's own id, when it was given one.",
          "$ref": "#/$defs/id"
        },
        "model": {
          "description": "Required for scope 'relation': relations live under the model that asserts them.",
          "$ref": "#/$defs/id"
        },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "type": { "$ref": "#/$defs/id" },
        "note": { "$ref": "#/$defs/noteDraft" }
      }
    },
    "noteDraft": {
      "description": "One note to write. CLOSED, unlike the note in mage-model.schema.json: that one is open because an unquoted comma in YAML flow style truncates a hand-written note and leaves a stray key, which the ANNOTATION rule reports. An op builds the note programmatically, so a stray key here is a caller's bug and is refused rather than written and then reported.",
      "type": "object",
      "required": ["kind", "text"],
      "additionalProperties": false,
      "properties": {
        "id": {
          "description": "Addressing handle for the note; an ANNOTATION finding cites it. Defaults to note-<n> for the next free n at this target.",
          "$ref": "#/$defs/id"
        },
        "kind": { "enum": ["comment", "rationale", "assumption", "question", "todo"] },
        "text": {
          "description": "Non-empty: canonicalization DROPS a note with empty text, so writing one would report success and store nothing.",
          "type": "string",
          "minLength": 1
        },
        "author": {
          "description": "Which side wrote it -- human or agent. Not an identity claim.",
          "type": "string"
        },
        "at": {
          "description": "Timestamp, QUOTED. A bare one is coerced by a YAML 1.1 loader (SEMANTICS.md 10.1).",
          "type": "string"
        }
      }
    },
    "opSetEntityType": {
      "description": "Author an existing entity's `type:` -- the establishment act SEMANTICS.md 3.3 requires before a declared relation type will accept the entity at a constrained end. Typing is how an author asserts a kind, so it is an ordinary authored edit: an agent naming a kind it has not been told is asserting a semantic fact nobody established, which 3.3's lab lesson names as the wrong move.",
      "type": "object",
      "required": ["op", "id", "value"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "set-entity-type" },
        "id": { "$ref": "#/$defs/id" },
        "value": { "type": "string", "minLength": 1 }
      }
    },
    "opSaveQuery": {
      "type": "object",
      "required": ["op", "id", "query"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "save-query" },
        "id": { "$ref": "#/$defs/id" },
        "query": {
          "description": "Conforms to mage-query.schema.json.",
          "type": "object"
        }
      }
    },
    "opDeleteQuery": {
      "type": "object",
      "required": ["op", "id"],
      "additionalProperties": false,
      "properties": {
        "op": { "const": "delete-query" },
        "id": { "$ref": "#/$defs/id" }
      }
    }
  }
}
